import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowUp, Check, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { PublishSessionDialog } from '@/components/custom/ogsm/publish-session-dialog'
import { ReopenSessionDialog } from '@/components/custom/ogsm/reopen-session-dialog'
import { isScorecardEnabled } from '@/configs/feature-flags'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { formatDate } from '@/lib/format'
import type { OgsmBoard } from '@/types/api'

export interface SessionBarProps {
  companyId: string
  board: OgsmBoard
}

/** The session's state and its Publish / Reopen action. The caller decides when it shows. */
export function SessionBar({ companyId, board }: SessionBarProps) {
  const { session } = board
  const [dialog, setDialog] = useState<'publish' | 'reopen' | null>(null)
  const [note, setNote] = useState<'published' | 'reopened' | null>(null)
  const published = session.status === 'PUBLISHED'
  const closeDialog = (open: boolean) => {
    if (!open) setDialog(null)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3.5 rounded-md border border-primary/20 bg-white px-4 py-2.5">
        <Typography
          as="span"
          variant="body-sm"
          className="shrink-0 font-semibold"
        >
          {OGSM_COPY.sessionBarLabel(session.fiscalYear)}
        </Typography>
        {published ? (
          <Typography
            as="span"
            variant="overline"
            className="shrink-0 rounded-md border border-primary bg-primary px-2 py-0.5 text-primary-foreground"
          >
            {OGSM_COPY.publishedState}
          </Typography>
        ) : (
          <Typography
            as="span"
            variant="overline"
            className="shrink-0 rounded-md border border-secondary/45 bg-secondary/10 px-2 py-0.5 text-secondary"
          >
            {OGSM_COPY.draftState}
          </Typography>
        )}
        <Typography
          variant="body-sm"
          className="min-w-50 flex-1 text-primary/60"
        >
          {published
            ? OGSM_COPY.publishedNote(
                session.publishedAt ? formatDate(session.publishedAt) : null,
                session.publishedKpiCount
              )
            : OGSM_COPY.draftNote(session.flaggedCount, session.objectiveCount)}
        </Typography>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {isScorecardEnabled() && (
            <Button asChild variant="link" size="sm">
              <Link
                to="/companies/$companyId"
                params={{ companyId }}
                search={{ tab: 'kpis' }}
              >
                {OGSM_COPY.viewScorecard}
              </Link>
            </Button>
          )}
          {published ? (
            <Button
              variant="outline"
              size="sm"
              title={OGSM_COPY.reopenHint}
              onClick={() => setDialog('reopen')}
            >
              <Undo2 />
              {OGSM_COPY.reopenAsDraft}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              className="text-white hover:text-white"
              onClick={() => setDialog('publish')}
            >
              <ArrowUp />
              {OGSM_COPY.publishSession}
            </Button>
          )}
        </div>
      </div>
      {note && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2"
        >
          <Check className="size-4 shrink-0 text-primary" aria-hidden />
          <Typography variant="body-sm" className="flex-1">
            {note === 'published'
              ? OGSM_COPY.publishedSuccess(session.fiscalYear)
              : OGSM_COPY.reopenedSuccess(session.fiscalYear)}
          </Typography>
          <Button
            variant="ghost"
            size="sm"
            className="text-primary/45"
            onClick={() => setNote(null)}
          >
            {OGSM_COPY.dismiss}
          </Button>
        </div>
      )}
      <PublishSessionDialog
        companyId={companyId}
        board={board}
        open={dialog === 'publish'}
        onOpenChange={closeDialog}
        onPublished={() => setNote('published')}
      />
      <ReopenSessionDialog
        companyId={companyId}
        board={board}
        open={dialog === 'reopen'}
        onOpenChange={closeDialog}
        onReopened={() => setNote('reopened')}
      />
    </div>
  )
}
